import { useSyncExternalStore } from 'react';

/**
 * Publishes the active tab of the page-level `TabSubrouter` so the Ask-Moo
 * screen context can forward it to the chat agent as `subTab`. The agent scopes
 * its answer and tool calls to the tab the user is actually on (Agencies →
 * Wholesale, Submissions → Portal, Claims → …).
 *
 * Route parsing alone can't recover the human tab label — the path segment is
 * `all` but the tab is "All Submissions", `api` but the tab is "API". The
 * `TabSubrouter` owns the real `label`, so it publishes it here rather than
 * making the Ask-Moo layer maintain a parallel path→label map per view.
 *
 * `baseUrl` is stored alongside the label so a consumer can confirm the value
 * belongs to the page it's currently on (see `clearActivePageTab` and the
 * consumer in `route-context-source.ts`) — during a route transition a
 * just-unmounted tab bar and a newly-mounted one briefly coexist.
 *
 * Backed by a globalThis singleton for the same reason as
 * `ask-moo/v2/cmail-selection-store`: one instance across duplicate module
 * evaluations under the test runner. In the app there is only ever one.
 */
const KEY = '__cowbellActivePageTabStore__';

interface Store {
  tabLabel: string | undefined;
  baseUrl: string | undefined;
  listeners: Set<() => void>;
}

const g = globalThis as unknown as Record<string, Store | undefined>;
const store: Store = (g[KEY] ??= {
  tabLabel: undefined,
  baseUrl: undefined,
  listeners: new Set(),
});

function emit() {
  store.listeners.forEach((l) => l());
}

export function setActivePageTab(
  tabLabel: string | undefined,
  baseUrl: string | undefined
): void {
  if (store.tabLabel === tabLabel && store.baseUrl === baseUrl) return;
  store.tabLabel = tabLabel;
  store.baseUrl = baseUrl;
  emit();
}

/**
 * Clear the active tab, but only when `baseUrl` still owns the slot. On a
 * route change the outgoing tab bar unmounts (cleanup) right after the incoming
 * one mounts and sets its own value; without the ownership guard the stale
 * cleanup would wipe the fresh entry.
 */
export function clearActivePageTab(baseUrl: string | undefined): void {
  if (store.baseUrl !== baseUrl) return;
  if (store.tabLabel === undefined && store.baseUrl === undefined) return;
  store.tabLabel = undefined;
  store.baseUrl = undefined;
  emit();
}

export interface ActivePageTab {
  tabLabel: string | undefined;
  baseUrl: string | undefined;
}

// useSyncExternalStore requires a referentially-stable snapshot when nothing
// changed, or it re-renders forever. Cache the object and only rebuild it when
// a field actually differs.
let snapshot: ActivePageTab = { tabLabel: undefined, baseUrl: undefined };
function getSnapshot(): ActivePageTab {
  if (
    snapshot.tabLabel !== store.tabLabel ||
    snapshot.baseUrl !== store.baseUrl
  ) {
    snapshot = { tabLabel: store.tabLabel, baseUrl: store.baseUrl };
  }
  return snapshot;
}

function subscribe(cb: () => void): () => void {
  store.listeners.add(cb);
  return () => {
    store.listeners.delete(cb);
  };
}

export function useActivePageTab(): ActivePageTab {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
